"use client";

import React from 'react';
import { Booking } from '@/domain/entities/booking.entity';
import { format } from 'date-fns';
import { formatMYR } from '@/shared/utils/currency';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import {
    Calendar,
    Clock,
    User,
    Mail,
    Phone,
    Package as PackageIcon,
    CheckCircle2,
    XCircle,
    Clock3,
    Image as ImageIcon,
    Gift
} from 'lucide-react';

interface BookingDetailsCardProps {
    booking: Booking;
}

export const BookingDetailsCard: React.FC<BookingDetailsCardProps> = ({
    booking
}) => {
    const selectedPackage = booking.package;

    const getStatusConfig = (status: string) => {
        switch (status) {
            case 'APPROVED':
                return {
                    label: 'Approved',
                    icon: CheckCircle2,
                    className: 'bg-green-500/10 text-green-600 border-green-500/20'
                };
            case 'REJECTED':
                return {
                    label: 'Rejected',
                    icon: XCircle,
                    className: 'bg-red-500/10 text-red-600 border-red-500/20'
                };
            default:
                return {
                    label: 'Pending',
                    icon: Clock3,
                    className: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20'
                };
        }
    };

    const statusConfig = getStatusConfig(booking.status);
    const StatusIcon = statusConfig.icon;

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full max-w-2xl mx-auto bg-card/50 backdrop-blur-xl rounded-[2.5rem] border border-border/40 shadow-xl overflow-hidden"
        >
            {/* Header */}
            <div className="p-8 border-b border-border/40 bg-muted/20 flex flex-col items-center gap-4 text-center">
                <div className={cn(
                    "flex items-center gap-2 px-4 py-1.5 rounded-full border text-xs font-bold uppercase tracking-widest",
                    statusConfig.className
                )}>
                    <StatusIcon className="w-3.5 h-3.5" />
                    {statusConfig.label}
                </div>
                <div>
                    <h2 className="text-2xl font-serif text-foreground">Booking Details</h2>
                    <p className="text-sm text-muted-foreground font-mono mt-1">ID: {booking.id}</p>
                </div>
            </div>

            <div className="p-8 space-y-8">
                {/* Customer Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                        <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60">
                            <User className="w-3 h-3" />
                            Customer Information
                        </div>
                        <div className="space-y-3">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                    <User className="w-4 h-4 text-primary" />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-xs text-muted-foreground">Name</span>
                                    <span className="text-sm font-medium">{booking.customer.name}</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                    <Mail className="w-4 h-4 text-primary" />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-xs text-muted-foreground">Email</span>
                                    <span className="text-sm font-medium">{booking.customer.email}</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                    <Phone className="w-4 h-4 text-primary" />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-xs text-muted-foreground">Phone</span>
                                    <span className="text-sm font-medium">{booking.customer.phone}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Package Info */}
                    <div className="space-y-4">
                        <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60">
                            <PackageIcon className="w-3 h-3" />
                            Package Selected
                        </div>
                        {selectedPackage ? (
                            <div className="bg-card border border-border/50 rounded-2xl p-4 space-y-2">
                                <h3 className="font-serif text-lg">{selectedPackage.name}</h3>
                                <p className="text-xs text-muted-foreground line-clamp-2">
                                    {selectedPackage.description || 'No package description available.'}
                                </p>
                                <div className="pt-2 flex items-center gap-2">
                                    <span className="text-sm font-bold text-primary">
                                        {formatMYR(selectedPackage.finalPrice)}
                                    </span>
                                    <span className="text-[10px] bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">
                                        {selectedPackage.durationMinutes} Mins
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <p className="text-sm text-destructive">Package information unavailable</p>
                        )}
                    </div>
                </div>

                {/* Slots */}
                <div className="space-y-4 pt-6 border-t border-border/40">
                    <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60">
                        <Calendar className="w-3 h-3" />
                        Scheduled Sessions
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {booking.slots.map((slot, index) => {
                            const theme = slot.theme;
                            return (
                                <div key={index} className="flex items-start gap-4 p-4 bg-muted/30 rounded-2xl border border-border/30">
                                    <div className="flex flex-col items-center justify-center w-12 h-12 bg-card rounded-xl border border-border/50 shadow-sm shrink-0">
                                        <span className="text-[10px] font-bold text-primary uppercase">
                                            {format(new Date(slot.date), 'MMM')}
                                        </span>
                                        <span className="text-lg font-bold leading-none">
                                            {format(new Date(slot.date), 'd')}
                                        </span>
                                    </div>
                                    <div className="space-y-1">
                                        <div className="flex items-center gap-2">
                                            <Clock className="w-3 h-3 text-muted-foreground" />
                                            <span className="text-sm font-bold">{slot.time}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                            <ImageIcon className="w-3 h-3" />
                                            <span>{theme?.name || 'Theme information unavailable'}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Addons */}
                {booking.addons.length > 0 && (
                    <div className="space-y-4 pt-6 border-t border-border/40">
                        <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60">
                            <Gift className="w-3 h-3" />
                            Additional Services
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {booking.addons.map((item, index) => {
                                const addon = item.addon;
                                return (
                                    <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-xl border border-border/30">
                                        <div className="flex flex-col">
                                            <span className="text-sm font-medium">{addon?.name || 'Add-on information unavailable'}</span>
                                            <span className="text-xs text-muted-foreground">Qty: {item.quantity}</span>
                                        </div>
                                        <div className="text-sm font-bold text-primary">
                                            {formatMYR((addon?.price || 0) * item.quantity)}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Total Price */}
                <div className="pt-8 border-t border-border/40 flex justify-between items-end">
                    <div className="flex flex-col gap-1">
                        <span className="text-xs text-muted-foreground uppercase tracking-widest">Total Amount</span>
                        <span className="text-[10px] text-muted-foreground/60">Included all taxes & fees</span>
                    </div>
                    <div className="text-4xl font-serif text-foreground">
                        {formatMYR(booking.totalPrice)}
                    </div>
                </div>
            </div>
        </motion.div>
    );
};
